// National-rollup bar — counting-phase recap strip that sits
// directly under the live-band (full-width like the other
// .live-band sections), running across the width of the map +
// live-feed columns above it.
//
// This is the cross-district companion to DistrictGrid (the
// "Who's Elected" by-district widget below it): both surfaces
// answer "who's elected?", but at different scales.
//
// Two things in one bar:
//   1. District selector — dropdown of districts where at least
//      one candidate has been elected, plus an "All districts" view
//      showing the most-recently-elected list across the country
//      (this is the default — the headline national-rollup
//      experience).
//   2. Carousel of elected candidates in the selected scope,
//      ordered by election round (most recent first). Each card has
//      a candidate photo slot (placeholder for now — editor will
//      upload the photo later), name + party chip, the count number
//      they were elected at, and the running total at that point.
//
// Data source: latestElectedAcrossDistricts() in
// electionday-results-types.ts — the cross-district derived list
// that already takes care of the override-elect-before-totals-typed
// case (electedAtRound = 1, votes = 0 fires immediately when the
// editor clicks ✓ on the count-sheet admin).

import { useMemo, useState } from 'react';
import useSWR from 'swr';
import type { CandidateRow } from '../../db/types';
import { useResultsData } from './useResultsData';
import { useResultsRoster, partyColour, partyChip } from './useResultsRoster';
import {
  DISTRICT_NUMBERS,
  SEATS_PER_DISTRICT,
  deriveDistrictCount,
  latestElectedAcrossDistricts,
  type CandidateLookup,
  type LatestElectedEntry,
} from '../../data/electionday-results-types';
import './WhosElectedBar.css';

interface CandidatesApi {
  candidates: CandidateRow[];
}

const candidatesFetcher = async (url: string): Promise<CandidatesApi> => {
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as CandidatesApi;
};

/** Selectable scopes for the dropdown. `null` = all districts
 *  (cross-country most-recent-first). */
type Scope = number | 'all';

export default function WhosElectedBar() {
  const { data } = useResultsData();
  const { byDistrict } = useResultsRoster();
  // We need the full candidate-row (party) for the lookup that
  // latestElectedAcrossDistricts wants, NOT the canonical-party
  // roster — the helper takes the raw `party` field and lets the
  // caller decide how to render it (we map to canonical via
  // partyChip / partyColour at render time).
  const { data: candData } = useSWR<CandidatesApi>(
    '/api/candidates',
    candidatesFetcher,
    { revalidateOnFocus: true, dedupingInterval: 60_000 },
  );

  // Build the per-district roster map + name/party lookup the
  // derivation helper expects. Inline construction (cheap on the
  // 13-district scale; useMemo prevents needless re-runs).
  const { rosterByDistrict, lookup } = useMemo(() => {
    const rd = new Map<number, readonly string[]>();
    const lk: CandidateLookup = new Map();
    for (const n of DISTRICT_NUMBERS) {
      const ids = byDistrict(n).map((c) => c.id);
      rd.set(n, ids);
    }
    for (const c of candData?.candidates ?? []) {
      lk.set(String(c.id), {
        full_name: c.display_name?.trim() || c.full_name,
        party: c.party,
      });
    }
    return { rosterByDistrict: rd, lookup: lk };
  }, [byDistrict, candData]);

  // The cross-district elected list, most-recent-first.
  const allElected: LatestElectedEntry[] = useMemo(
    () => latestElectedAcrossDistricts(data, rosterByDistrict, lookup),
    [data, rosterByDistrict, lookup],
  );

  // Default scope: 'all' — first thing the editor sees is the
  // most-recently-elected list across the country.
  const [scope, setScope] = useState<Scope>('all');

  // The visible list for the selected scope.
  const visible = useMemo(() => {
    if (scope === 'all') return allElected;
    return allElected.filter((e) => e.districtId === scope);
  }, [scope, allElected]);

  // Per-district status + elected count for the pill row. Drives the
  // dot colour (idle / in-progress / complete) so the reader sees at
  // a glance which districts have activity — same status logic the
  // old DistrictGrid used. allElected is the rollup; deriveDistrictCount
  // gives status from the editorial state.
  const perDistrict = useMemo(() => {
    return DISTRICT_NUMBERS.map((n) => {
      const ids = byDistrict(n).map((c) => c.id);
      const derived = deriveDistrictCount(n, data.districts[String(n)], ids);
      return {
        districtId: n,
        electedCount: allElected.filter((e) => e.districtId === n).length,
        status: derived.status,
      };
    });
  }, [data, byDistrict, allElected]);

  return (
    <section className="whos-elected" aria-label="National rollup of elected candidates">
      <header className="whos-elected__head">
        <div>
          <p className="whos-elected__kicker">National rollup</p>
          <h2 className="whos-elected__title">
            {scope === 'all'
              ? `Across the country · ${allElected.length} seat${allElected.length === 1 ? '' : 's'} filled`
              : `District ${scope} · ${visible.length} seat${visible.length === 1 ? '' : 's'} filled`}
          </h2>
        </div>
      </header>

      {/* District pill row — All + 13 districts. Each district pill
          carries a status dot (idle / in-progress / complete) and a
          tiny seats-filled badge so the editor sees activity without
          having to click. Replaces the previous dropdown selector. */}
      <div
        className="whos-elected__pills"
        role="tablist"
        aria-label="Pick a district"
      >
        <button
          type="button"
          role="tab"
          aria-selected={scope === 'all'}
          className="whos-elected__pill whos-elected__pill--all"
          onClick={() => setScope('all')}
        >
          All districts
          <span className="whos-elected__pill-badge">{allElected.length}</span>
        </button>
        {perDistrict.map((d) => {
          const dotCls =
            d.status === 'complete'
              ? 'whos-elected__pill-dot--complete'
              : d.status === 'in_progress'
                ? 'whos-elected__pill-dot--in-progress'
                : '';
          return (
            <button
              key={d.districtId}
              type="button"
              role="tab"
              aria-selected={scope === d.districtId}
              className="whos-elected__pill"
              onClick={() => setScope(d.districtId)}
              title={`District ${d.districtId} · ${d.electedCount} of ${SEATS_PER_DISTRICT} seats`}
            >
              D{d.districtId}
              <span
                className={`whos-elected__pill-dot ${dotCls}`}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>

      {visible.length === 0 ? (
        <p className="whos-elected__empty">
          {scope === 'all'
            ? 'No seats filled yet. Cards will appear here as candidates are called by the newsroom on /admin/results.'
            : `No seats filled in District ${scope} yet.`}
        </p>
      ) : (
        <ol className="whos-elected__list" role="list">
          {visible.map((e) => (
            <ElectedCard key={`${e.districtId}-${e.candidateId}`} entry={e} />
          ))}
        </ol>
      )}

      {/* Editor note — image upload is a follow-up, not built yet. */}
      <p className="whos-elected__note">
        Candidate photos will be uploadable from <a href="/admin/candidates">/admin/candidates</a> in a follow-up — the placeholder face slot is the spot they&apos;ll land.
      </p>
    </section>
  );
}

// ── One elected-candidate card ─────────────────────────────────────

function ElectedCard({ entry }: { entry: LatestElectedEntry }) {
  // Canonicalise the party for chip + colour at render time. The
  // helper deliberately keeps party as the raw DB string so this
  // surface (and any future carousel) can apply its own rendering
  // policy without the data layer having an opinion.
  const colour = partyColour(canonicalise(entry.party));
  const chip = partyChip(canonicalise(entry.party));
  return (
    <li className="whos-elected__card" style={{ ['--card-accent' as string]: colour }}>
      {/* Photo slot — placeholder for now, editor uploads later. */}
      <div className="whos-elected__photo" aria-hidden="true">
        <span className="whos-elected__photo-initial">
          {initials(entry.candidateName)}
        </span>
      </div>
      <div className="whos-elected__body">
        <p className="whos-elected__district">
          District {entry.districtId} · Count {entry.electedAtRound}
        </p>
        <p className="whos-elected__name">{entry.candidateName}</p>
        <div className="whos-elected__meta">
          <span
            className="whos-elected__chip"
            style={{ background: colour }}
          >
            {chip}
          </span>
          {entry.votes > 0 && (
            <span className="whos-elected__votes">
              {entry.votes.toLocaleString()} votes
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0].slice(0, 1) + parts[parts.length - 1].slice(0, 1)).toUpperCase();
}

/** Lightweight party-string canonicalisation just for THIS file's
 *  chip / colour rendering. Mirrors the rules used elsewhere in the
 *  results surfaces without dragging the full normalisePartyName
 *  import chain in (it lives behind the /api boundary). Falls
 *  through to 'IND' for anything unrecognised. */
function canonicalise(raw: string): 'PL' | 'PN' | 'ADPD' | 'Momentum' | 'Imperium' | 'Aħwa Maltin' | 'IND' | 'Other' {
  const s = raw.trim();
  if (/laburist/i.test(s) || /^PL\b/.test(s)) return 'PL';
  if (/nazzjonalist|nationalist/i.test(s) || /^PN\b/.test(s)) return 'PN';
  if (/^AD\+?PD$/i.test(s) || /alternattiva/i.test(s)) return 'ADPD';
  if (/momentum/i.test(s)) return 'Momentum';
  if (/imperium/i.test(s)) return 'Imperium';
  if (/aħwa|ahwa maltin/i.test(s)) return 'Aħwa Maltin';
  if (/^IND$|independent/i.test(s)) return 'IND';
  return 'Other';
}
